import React, { useEffect, useState, useCallback, useMemo } from 'react';
import request from '../utils/request';

const SIGNUP_SOURCES = [
  { value: '', label: 'All sources' },
  { value: 'host_link', label: 'Host link' },
  { value: 'property_code', label: 'Property code' },
  { value: 'manual_address', label: 'Manual address' },
  { value: 'direct', label: 'Direct' },
  { value: 'unknown', label: 'Unknown' },
];

const FUNNEL_ORDER = [
  'screen_view',
  'service_view',
  'add_to_cart',
  'booking_started',
  'booking_completed',
];

const EVENT_COLORS = {
  session_start: 'bg-sky-50 text-sky-700',
  session_end: 'bg-zinc-100 text-zinc-500',
  screen_view: 'bg-zinc-50 text-zinc-600',
  service_view: 'bg-blue-50 text-blue-700',
  add_to_cart: 'bg-amber-50 text-amber-700',
  booking_started: 'bg-indigo-50 text-indigo-700',
  booking_completed: 'bg-emerald-50 text-emerald-700',
  chat_opened: 'bg-purple-50 text-purple-700',
  ai_message: 'bg-fuchsia-50 text-fuchsia-700',
  tab_switch: 'bg-zinc-50 text-zinc-500',
  search: 'bg-teal-50 text-teal-700',
  funnel_step: 'bg-orange-50 text-orange-700',
  drop_off: 'bg-red-50 text-red-600',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const fmtDay = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const OverviewCard = ({ label, value, sub }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4">
    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
    <p className="text-2xl font-bold text-zinc-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
  </div>
);

/* ─── Per-guest drawer with event timeline ─── */
const GuestDrawer = ({ guestId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request
      .get(`/admin/guests/${guestId}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.error || 'Failed to load guest'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [guestId]);

  const funnel = useMemo(() => {
    const byType = new Map((data?.eventCountsByType || []).map((r) => [r.type, r.count]));
    return FUNNEL_ORDER.map((t) => ({ type: t, count: byType.get(t) || 0 }));
  }, [data]);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">
              {loading ? 'Loading…' : `${data?.guest?.firstName || ''} ${data?.guest?.lastName || ''}`.trim() || data?.guest?.email}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">{data?.guest?.email}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-500">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-zinc-400">Loading guest…</div>}
          {err && <div className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{err}</div>}

          {data?.guest && (
            <>
              {/* Profile */}
              <div className="px-5 py-4 border-b border-zinc-100 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Phone</p>
                    <p className="text-zinc-900">{data.guest.phoneNumber || '—'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Signup source</p>
                    <p className="text-zinc-900">{data.guest.signupSource || 'unknown'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Signed up</p>
                    <p className="text-zinc-900">{fmtDay(data.guest.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Last active</p>
                    <p className="text-zinc-900">{fmtDate(data.guest.stats?.lastActiveAt)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Sessions</p>
                    <p className="text-zinc-900 font-bold">{data.guest.stats?.totalSessions || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Bookings</p>
                    <p className="text-zinc-900 font-bold">{data.guest.stats?.totalBookings || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">LTV</p>
                    <p className="text-zinc-900 font-bold">{fmtMoney(data.guest.stats?.totalSpendCents)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Device</p>
                    <p className="text-zinc-900">
                      {[data.guest.device?.os, data.guest.device?.appVersion].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                </div>

                {(data.guest.address?.city || data.guest.address?.line1) && (
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Address</p>
                    <p className="text-zinc-900">
                      {data.guest.address.line1 ? `${data.guest.address.line1}, ` : ''}
                      {[data.guest.address.city, data.guest.address.state, data.guest.address.zip].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}

                {data.guest.entryInstructions && (
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Entry instructions</p>
                    <p className="text-zinc-900 text-sm whitespace-pre-wrap">{data.guest.entryInstructions}</p>
                  </div>
                )}

                {Array.isArray(data.guest.interests) && data.guest.interests.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider mb-1">Interests</p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.guest.interests.map((i) => (
                        <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{i}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Funnel */}
              <div className="px-5 py-4 border-b border-zinc-100">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Funnel (all-time)</h3>
                <div className="space-y-1.5">
                  {funnel.map((step, idx) => {
                    const max = funnel[0].count || 1;
                    const pct = Math.max(2, Math.round((step.count / max) * 100));
                    return (
                      <div key={step.type} className="flex items-center gap-2 text-xs">
                        <span className="w-32 text-zinc-600">{step.type.replace(/_/g, ' ')}</span>
                        <div className="flex-1 h-5 bg-zinc-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-indigo-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-10 text-right text-zinc-700 font-medium">{step.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent service requests */}
              {data.recentRequests?.length > 0 && (
                <div className="px-5 py-4 border-b border-zinc-100">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
                    Recent service requests ({data.recentRequests.length})
                  </h3>
                  <div className="space-y-2">
                    {data.recentRequests.map((r) => (
                      <div key={r._id} className="text-sm border border-zinc-100 rounded-lg p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-zinc-900">
                            {(r.servicesRequested || []).join(', ') || 'Service Request'}
                          </span>
                          <span className="text-xs text-zinc-400">{fmtDate(r.createdAt)}</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {r.status}{r.paymentStatus ? ` · ${r.paymentStatus}` : ''}{r.price ? ` · $${r.price.toFixed(2)}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Event timeline */}
              <div className="px-5 py-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
                  Recent events ({data.recentEvents?.length || 0})
                </h3>
                {(!data.recentEvents || data.recentEvents.length === 0) && (
                  <p className="text-sm text-zinc-400 text-center py-6">No events recorded yet</p>
                )}
                <div className="space-y-2">
                  {(data.recentEvents || []).map((ev) => (
                    <div key={ev._id} className="flex items-start gap-3 text-sm">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${EVENT_COLORS[ev.type] || 'bg-zinc-50 text-zinc-600'}`}>
                        {ev.type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-800 truncate">
                          {ev.screen && <span className="text-zinc-500 text-xs">{ev.screen} · </span>}
                          <span className="text-xs text-zinc-600">
                            {ev.props ? Object.entries(ev.props)
                              .filter(([, v]) => v != null && v !== '')
                              .slice(0, 4)
                              .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 40) : String(v).slice(0, 40)}`)
                              .join(' · ') : ''}
                          </span>
                        </p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">{fmtDate(ev.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ─── Main screen ─── */
const GuestEngagementScreen = () => {
  const [overview, setOverview] = useState(null);
  const [guests, setGuests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGuestId, setSelectedGuestId] = useState(null);

  const [filters, setFilters] = useState({
    q: '',
    signupSource: '',
    city: '',
    state: '',
    hasStay: '',
    lastActiveInDays: '',
    minBookings: '',
    sort: '-stats.lastActiveAt',
    limit: 50,
    skip: 0,
  });

  const [windowDays, setWindowDays] = useState(30);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await request.get(`/admin/guests/analytics/overview?days=${windowDays}`);
      setOverview(data);
    } catch (_) {}
  }, [windowDays]);

  const fetchGuests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) params.set(k, String(v));
      });
      const { data } = await request.get(`/admin/guests?${params.toString()}`);
      setGuests(data.guests || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  const updateFilter = (patch) => setFilters((f) => ({ ...f, ...patch, skip: 0 }));

  const funnelOverview = useMemo(() => {
    const byType = new Map((overview?.eventsByType || []).map((r) => [r.type, r]));
    const max = FUNNEL_ORDER.reduce((m, t) => Math.max(m, byType.get(t)?.uniqueGuests || 0), 0) || 1;
    return FUNNEL_ORDER.map((t) => ({
      type: t,
      count: byType.get(t)?.count || 0,
      uniqueGuests: byType.get(t)?.uniqueGuests || 0,
      pct: Math.max(2, Math.round(((byType.get(t)?.uniqueGuests || 0) / max) * 100)),
    }));
  }, [overview]);

  return (
    <div className="flex-1 overflow-auto bg-zinc-50 p-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Guest Engagement</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {total.toLocaleString()} guests · {overview?.activeGuests?.toLocaleString() || 0} active last {windowDays} days
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <button
              onClick={() => { fetchOverview(); fetchGuests(); }}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <OverviewCard label="Total guests" value={(overview?.totalGuests || 0).toLocaleString()} />
          <OverviewCard label={`New (${windowDays}d)`} value={(overview?.newGuests || 0).toLocaleString()} />
          <OverviewCard label={`Active (${windowDays}d)`} value={(overview?.activeGuests || 0).toLocaleString()} />
          <OverviewCard label="Have booked" value={(overview?.bookingGuests || 0).toLocaleString()} />
        </div>

        {/* Funnel + signup sources */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
              Funnel — unique guests (last {windowDays} days)
            </h3>
            <div className="space-y-2">
              {funnelOverview.map((step) => (
                <div key={step.type} className="flex items-center gap-3 text-sm">
                  <span className="w-36 text-zinc-600 capitalize">{step.type.replace(/_/g, ' ')}</span>
                  <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                    <div className="h-full bg-indigo-500 flex items-center px-2 text-white text-xs font-semibold" style={{ width: `${step.pct}%` }}>
                      {step.uniqueGuests}
                    </div>
                  </div>
                  <span className="w-20 text-right text-xs text-zinc-400">{step.count} events</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Signup sources</h3>
            {overview?.signupSources?.length ? (
              <div className="space-y-2">
                {overview.signupSources.map((s) => {
                  const maxSrc = overview.signupSources[0]?.count || 1;
                  const pct = Math.max(2, Math.round((s.count / maxSrc) * 100));
                  return (
                    <div key={s.source} className="flex items-center gap-2 text-sm">
                      <span className="w-28 text-zinc-600 text-xs capitalize">{s.source?.replace(/_/g, ' ')}</span>
                      <div className="flex-1 h-4 bg-zinc-100 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right text-xs text-zinc-500">{s.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 text-center py-4">No recent signups</p>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-2">
          <input
            type="text"
            placeholder="Search name/email/phone"
            value={filters.q}
            onChange={(e) => updateFilter({ q: e.target.value })}
            className="col-span-2 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={filters.signupSource}
            onChange={(e) => updateFilter({ signupSource: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            {SIGNUP_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="City"
            value={filters.city}
            onChange={(e) => updateFilter({ city: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg"
          />
          <input
            type="text"
            placeholder="State (2-letter)"
            value={filters.state}
            onChange={(e) => updateFilter({ state: e.target.value.toUpperCase().slice(0, 2) })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg"
          />
          <select
            value={filters.hasStay}
            onChange={(e) => updateFilter({ hasStay: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            <option value="">Any stay</option>
            <option value="true">Has stay</option>
            <option value="false">No stay</option>
          </select>
          <select
            value={filters.lastActiveInDays}
            onChange={(e) => updateFilter({ lastActiveInDays: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            <option value="">Any activity</option>
            <option value="1">Active last 24h</option>
            <option value="7">Active last 7 days</option>
            <option value="30">Active last 30 days</option>
            <option value="90">Active last 90 days</option>
          </select>
          <select
            value={filters.minBookings}
            onChange={(e) => updateFilter({ minBookings: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            <option value="">Any bookings</option>
            <option value="1">1+ bookings</option>
            <option value="2">2+ bookings</option>
            <option value="5">5+ bookings</option>
          </select>
          <select
            value={filters.sort}
            onChange={(e) => updateFilter({ sort: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            <option value="-stats.lastActiveAt">Sort: Last active ↓</option>
            <option value="-stats.totalBookings">Sort: Bookings ↓</option>
            <option value="-stats.totalSpendCents">Sort: LTV ↓</option>
            <option value="-stats.totalSessions">Sort: Sessions ↓</option>
            <option value="-createdAt">Sort: Newest first</option>
            <option value="createdAt">Sort: Oldest first</option>
          </select>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

        {/* Guests table */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Guest</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Signup</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Location</th>
                  <th className="text-right px-4 py-3 font-semibold text-zinc-600">Sessions</th>
                  <th className="text-right px-4 py-3 font-semibold text-zinc-600">Bookings</th>
                  <th className="text-right px-4 py-3 font-semibold text-zinc-600">LTV</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Last active</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Interests</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12"><div className="w-6 h-6 mx-auto border-2 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" /></td></tr>
                ) : guests.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-zinc-400">No guests match these filters</td></tr>
                ) : guests.map((g) => (
                  <tr
                    key={g._id}
                    onClick={() => setSelectedGuestId(g._id)}
                    className="hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium text-zinc-900">
                            {`${g.firstName} ${g.lastName}`.trim() || g.email}
                          </p>
                          <p className="text-xs text-zinc-500">{g.email}</p>
                        </div>
                        {g.hasPushToken && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">PUSH</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600 text-xs capitalize">{(g.signupSource || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">
                      {[g.city, g.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-700">{g.totalSessions}</td>
                    <td className="px-4 py-3 text-right text-zinc-700 font-medium">{g.totalBookings}</td>
                    <td className="px-4 py-3 text-right text-zinc-900 font-medium">{fmtMoney(g.totalSpendCents)}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{fmtDate(g.lastActiveAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(g.interests || []).slice(0, 3).map((i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px]">{i}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {total > filters.limit && (
          <div className="flex items-center justify-between mt-3 text-sm text-zinc-500">
            <span>
              Showing {filters.skip + 1}–{Math.min(filters.skip + filters.limit, total)} of {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                disabled={filters.skip <= 0}
                onClick={() => setFilters((f) => ({ ...f, skip: Math.max(0, f.skip - f.limit) }))}
                className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-100"
              >
                Previous
              </button>
              <button
                disabled={filters.skip + filters.limit >= total}
                onClick={() => setFilters((f) => ({ ...f, skip: f.skip + f.limit }))}
                className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedGuestId && (
        <GuestDrawer guestId={selectedGuestId} onClose={() => setSelectedGuestId(null)} />
      )}
    </div>
  );
};

export default GuestEngagementScreen;
